import { useNavigate } from 'react-router-dom';
import React, { useRef, useState, useEffect } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import {
  Box,
  Chip,
  List,
  Stack,
  Divider,
  useTheme,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
  InputAdornment,
  ListItemButton,
  CircularProgress,
} from '@mui/material';

import useLocales from 'src/hooks/useLocales';
import useDebounce from 'src/hooks/useDebounce';

import { fDateTime } from 'src/utils/formatTime';

import { DocumentStatuses } from 'src/@types/documents/enums/documentStatuses';

import { fileThumb } from 'src/sections/documents/upload/utils';

import MenuPopover from '../../../components/MenuPopover';
import documentService from '../../../services/documentService';
import RenderStatusField from '../../../utils/folders/renderStatusField';

// ----------------------------------------------------------------------

const SUGGESTIONS_LIMIT = 5;
const DEBOUNCE_DELAY = 500;

type SearchResultItem = {
  id: number;
  name: string;
  extension: string;
  ownerFullName: string;
  created: string;
  status: DocumentStatuses;
  folderName?: string;
  tags?: Array<{ id: number; name: string; color: string }>;
  extractedText?: string;
};

type GlobalSearchProps = {
  id?: string;
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({ id }) => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const debouncedQuery = useDebounce(query, DEBOUNCE_DELAY);

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      setTotal(0);
      setIsOpen(false);
      return;
    }

    let isCancelled = false;

    const fetchResults = async () => {
      try {
        setIsLoading(true);
        const response = await documentService.globalSearch(debouncedQuery, 0, SUGGESTIONS_LIMIT);

        if (!isCancelled) {
          setResults(response?.items || []);
          setTotal(response?.total || 0);
          setIsOpen(true);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Global search error:', error);
          setResults([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      isCancelled = true;
    };
  }, [debouncedQuery]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleResultClick = (result: SearchResultItem) => {
    handleClose();
    navigate(`/standard/documents/${result.id}`);
  };

  const handleViewMore = () => {
    handleClose();
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const handleSearchToggle = () => {
    setIsSearchExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSearchClose = () => {
    setIsSearchExpanded(false);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  return (
    <Box
      ref={anchorRef}
      id={id}
      sx={[
        { flexShrink: 1, minWidth: 0, mx: isMobile ? 0 : 4 },
        isMobile &&
          isSearchExpanded && {
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            backgroundColor: 'background.paper',
          },
      ]}
    >
      {isMobile && !isSearchExpanded ? (
        <IconButton onClick={handleSearchToggle} sx={{ color: 'action.active' }}>
          <SearchIcon />
        </IconButton>
      ) : (
        <TextField
          inputRef={inputRef}
          sx={{ width: '100%', maxWidth: '500px' }}
          placeholder={t('globalSearch.search')}
          variant="standard"
          value={query}
          onChange={handleInputChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
              endAdornment: isLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : isMobile ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleSearchClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }
          }}
        />
      )}
      <MenuPopover
        open={isOpen && results.length > 0}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 600 }}
      >
        <List disablePadding sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {results.map((result) => (
            <ListItemButton
              key={result.id}
              onClick={() => handleResultClick(result)}
              sx={{ py: 1.5, px: 2, display: 'block' }}
            >
              {/* Row 1: Icon, Name, Status */}
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: "center",
                  mb: 0.5
                }}>
                <Box
                  component="img"
                  src={fileThumb(`file.${result.extension}`)}
                  sx={{ width: 28, height: 28, flexShrink: 0 }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {result.name || t('globalSearch.noTitle') || 'Untitled'}
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                  <RenderStatusField status={result.status} />
                </Box>
              </Stack>

              {/* Row 2: Author, Date, Folder */}
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center",
                  mb: 0.5,
                  color: 'text.secondary'
                }}>
                <Typography variant="caption">{result.ownerFullName}</Typography>
                <Typography variant="caption">{fDateTime(result.created)}</Typography>
                {result.folderName && (
                  <Stack direction="row" spacing={0.5} sx={{
                    alignItems: "center"
                  }}>
                    <FolderIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption">{result.folderName}</Typography>
                  </Stack>
                )}
              </Stack>

              {/* Row 3: Tags */}
              {result.tags && result.tags.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {result.tags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: tag.color || 'default',
                        color: 'white',
                      }}
                    />
                  ))}
                </Stack>
              )}

              {/* Row 4: Extracted text preview - highlight from API */}
              {result.extractedText && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    display: 'block',
                    '& mark': {
                      backgroundColor: 'warning.light',
                      fontWeight: 'bold',
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: result.extractedText }}
                />
              )}
            </ListItemButton>
          ))}
        </List>

        {total > results.length && (
          <>
            <Divider />
            <ListItemButton onClick={handleViewMore} sx={{ py: 1.5, justifyContent: 'center' }}>
              <Typography variant="subtitle2" color="primary">
                {t('globalSearch.showMoreResults') || 'View more'}
              </Typography>
            </ListItemButton>
          </>
        )}
      </MenuPopover>
    </Box>
  );
};

export default GlobalSearch;
